import React from 'react';
import { gettext } from '../../i18n';
import styles from './Image.module.scss';
import PlaceholderSvg1 from './placeholders/PlaceholderSvg1';
import PlaceholderSvg2 from './placeholders/PlaceholderSvg2';
import PlaceholderSvg3 from './placeholders/PlaceholderSvg3';
import PlaceholderSvg4 from './placeholders/PlaceholderSvg4';

/**
 * Collection image with placeholder fallbacks.
 *
 * Port notes vs eb-ui `Image.tsx`:
 * - `GREYS_JSON['grey-200']` (eds-color) → the literal `#EEEDF2` (verified in
 *   `eds-color/src/color_greys.json`).
 * - `eds-iconography`'s `PlaceholderSvg1..4` are ABSENT from Marmalade's icon
 *   set → the 4 SVGs are copied VERBATIM from
 *   `eb-ui/packages/eds/eds-iconography/src/icons/PlaceholderSvg*.tsx`
 *   (the catalog's inline-the-eb-ui-SVG pattern for iconography gaps).
 * - `eds-illustration`'s `Illustration` was a thin wrapper rendering the SVG
 *   through eds VectorImage at width/height 100% — replaced by rendering the
 *   SVG element directly with `width="100%" height="100%"`.
 */

const GREY_200 = '#EEEDF2';

export const FALLBACK_IMAGE_LIST = [
    <PlaceholderSvg1 key="fallback-image-1" width="100%" height="100%" />,
    <PlaceholderSvg2 key="fallback-image-2" width="100%" height="100%" />,
    <PlaceholderSvg3 key="fallback-image-3" width="100%" height="100%" />,
    <PlaceholderSvg4 key="fallback-image-4" width="100%" height="100%" />,
];

interface ImageProps {
    url?: string;
    backgroundColor?: string;
    name: string;
    id?: string;
}

export const Image: React.FunctionComponent<ImageProps> = (props) => {
    if (props.url) {
        return (
            <div
                data-testid="creator-collection-image"
                className={styles.aspectContainer}
                style={{
                    backgroundColor: props.backgroundColor
                        ? props.backgroundColor
                        : GREY_200,
                }}
            >
                <div className={styles.imagePosition}>
                    <img
                        loading="lazy"
                        className={styles.image}
                        src={props.url}
                        alt={gettext('Collection image for %(name)s', {
                            name: props.name,
                        })}
                    />
                </div>
            </div>
        );
    }

    const numberFromId = props.id ? parseInt(props.id, 10) % 4 : 0;
    const RandomImageSvg = FALLBACK_IMAGE_LIST[numberFromId];
    const fallbackImage = RandomImageSvg
        ? RandomImageSvg
        : FALLBACK_IMAGE_LIST[0];

    return (
        <div
            data-testid="fallback-collection-card-image"
            className={styles.aspectContainer}
        >
            <div className={styles.imagePosition}>{fallbackImage}</div>
        </div>
    );
};
